// FileUtils.js

/**
 * Formats the file size into a human-readable string.
 * @param {number} size - The size of the file in bytes.
 * @returns {string} - The formatted file size (e.g., "1.5 MB").
 */
export const formatFileSize = size => {
    if (size < 1024) return size + ' bytes';
    else if (size < 1048576) return (size / 1024).toFixed(2) + ' KB';
    else return (size / 1048576).toFixed(2) + ' MB';
};

/**
 * Gets the file extension from a file.
 * @param {string} filename - The file.
 * @returns {string} - The extension of the file (e.g., "mp3", "jpeg", "xls", "doc", "ppt").
 */
export const getFileExtension = file => {
    return file.name.split('.').pop().toLowerCase();
};

const FILE_CATEGORY_MAP = {
    video: ['mp4', 'mov'],
    audio: ['mp3', 'wav'],
    image: ['jpg', 'jpeg', 'png', 'svg'],
    spreadsheet: ['xls', 'xlsx', 'csv', 'numbers'],
    document: ['pdf', 'doc', 'docx', 'txt', 'ppt', 'pptx'],
};

const getFileCategoryByExtension = extension => {
    return (
        Object.keys(FILE_CATEGORY_MAP).find(category =>
            FILE_CATEGORY_MAP[category].includes(extension),
        ) || 'other'
    );
};

const getNormalizedFileTypeInternal = ({
    value,
    parseUrlPath = false,
    parseMimeType = false,
}) => {
    if (!value || typeof value !== 'string') {
        return '';
    }

    const normalizedValue = value
        .split('?')[0]
        .split('#')[0]
        .trim()
        .toLowerCase();

    if (!normalizedValue) {
        return '';
    }

    if (
        parseUrlPath &&
        (normalizedValue.startsWith('http://') ||
            normalizedValue.startsWith('https://'))
    ) {
        try {
            const parsedUrl = new URL(normalizedValue);
            const pathSegment = parsedUrl.pathname.split('/').pop() || '';
            if (pathSegment.includes('.')) {
                return pathSegment.split('.').pop();
            }
        } catch (error) {
            // no-op
        }
    }

    if (parseMimeType && normalizedValue.includes('/')) {
        const mimeSegments = normalizedValue.split('/');
        const mimeSubtype = mimeSegments[mimeSegments.length - 1];
        if (mimeSubtype) {
            return mimeSubtype;
        }
    }
    return normalizedValue.includes('.')
        ? normalizedValue.split('.').pop()
        : normalizedValue;
};

/**
 * Gets the category of a file based on its extension. Only supported included should be added here.
 * @param {string} filename - The name of the file including its extension.
 * @returns {string} - The category of the file (e.g., "document", "video", "audio", "image", "other").
 */
export const getFileCategory = filename => {
    const extension = filename?.split('.').pop().toLowerCase();
    const categoryFromExtension = getFileCategoryByExtension(extension);
    if (categoryFromExtension !== 'other') {
        return categoryFromExtension;
    }

    const normalizedExtension = getNormalizedFileTypeV2(filename);
    return getFileCategoryByExtension(normalizedExtension);
};

export const getFileCategoryV2 = filename => {
    const extension = getNormalizedFileTypeV2(filename);
    return getFileCategoryByExtension(extension);
};

export const BROWSER_PREVIEWABLE_FILE_TYPES = [
    'pdf',
    'png',
    'jpg',
    'jpeg',
    'gif',
    'txt',
    'mp4',
];

export const BROWSER_PREVIEWABLE_FILE_TYPES_V2 = [
    ...BROWSER_PREVIEWABLE_FILE_TYPES,
    'docx',
];

export const OFFICE_PREVIEWABLE_FILE_TYPES = ['ppt', 'pptx'];

export const getNormalizedFileType = value => {
    return getNormalizedFileTypeInternal({ value });
};

export const getNormalizedFileTypeV2 = value => {
    return getNormalizedFileTypeInternal({
        value,
        parseUrlPath: true,
        parseMimeType: true,
    });
};

export const canRenderWithOfficeViewer = (fileType, deployedEnvName) => {
    const normalizedFileType = getNormalizedFileType(fileType);
    if (!OFFICE_PREVIEWABLE_FILE_TYPES.includes(normalizedFileType)) {
        return false;
    }

    const normalizedEnv = (deployedEnvName || 'Local').toUpperCase();
    return normalizedEnv.includes('STAG') || normalizedEnv.includes('PROD');
};

export const canRenderAssetPreviewInBrowser = (fileType, deployedEnvName) => {
    const normalizedFileType = getNormalizedFileType(fileType);
    if (BROWSER_PREVIEWABLE_FILE_TYPES.includes(normalizedFileType)) {
        return true;
    }

    if (normalizedFileType === 'docx') {
        return true;
    }

    const normalizedFileTypeV2 = getNormalizedFileTypeV2(fileType);
    if (normalizedFileTypeV2 === 'docx') {
        return true;
    }

    return canRenderWithOfficeViewer(normalizedFileType, deployedEnvName);
};

export const canRenderAssetPreviewInBrowserV2 = (fileType, deployedEnvName) => {
    const normalizedFileType = getNormalizedFileTypeV2(fileType);
    if (BROWSER_PREVIEWABLE_FILE_TYPES_V2.includes(normalizedFileType)) {
        return true;
    }

    if (getFileCategoryV2(normalizedFileType) === 'video') {
        return true;
    }

    return canRenderWithOfficeViewer(normalizedFileType, deployedEnvName);
};

/**
 * Processes the file name for downloads by cleaning it up and adding the appropriate extension.
 * @param {string} name - The original name of the file.
 * @param {string} fileExtension - The file extension to append.
 * @returns {string} - The processed file name (e.g., "cleaned-name.pdf").
 */
export const processFileName = (name, fileExtension) => {
    const cleanedName = name
        .replace(/\s*-\s*/g, '-')
        .replace(/[\s,.]+/g, '_')
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/[^a-zA-Z0-9_\-()]/g, '')
        .trim();

    return `${cleanedName}.${fileExtension}`;
};

/**
 * Gets the runtime of a video file.
 * @param {File} file - The video file to analyze.
 * @returns {Promise<string>} - A promise that resolves to the formatted runtime (e.g., "1h 30m").
 */
export const getVideoRuntime = file => {
    return new Promise(resolve => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = URL.createObjectURL(file);
        video.onloadedmetadata = () => {
            const durationInSeconds = video.duration;
            const hours = Math.floor(durationInSeconds / 3600);
            const minutes = Math.floor((durationInSeconds % 3600) / 60);
            const seconds = Math.floor(durationInSeconds % 60);
            let formattedRuntime =
                hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${seconds}s`;
            resolve(formattedRuntime);
            URL.revokeObjectURL(video.src);
        };
    });
};

/**
 * Gets the length of an audio file.
 * @param {File} file - The audio file to analyze.
 * @returns {Promise<string>} - A promise that resolves to the formatted length (e.g., "3m 45s").
 */
export const getAudioLength = file => {
    return new Promise((resolve, reject) => {
        const audio = new Audio();
        audio.src = URL.createObjectURL(file);
        audio.onloadedmetadata = () => {
            const durationInSeconds = audio.duration;
            const hours = Math.floor(durationInSeconds / 3600);
            const minutes = Math.floor((durationInSeconds % 3600) / 60);
            const seconds = Math.floor(durationInSeconds % 60);
            let formattedLength =
                hours > 0
                    ? `${hours}h ${minutes}m ${seconds}s`
                    : `${minutes}m ${seconds}s`;
            resolve(formattedLength);
            URL.revokeObjectURL(audio.src);
        };
        audio.onerror = () => {
            reject(new Error('Error loading audio file'));
            URL.revokeObjectURL(audio.src);
        };
    });
};

/**
 * Gets the dimensions of an image file.
 * @param {File} file - The image file to analyze.
 * @returns {Promise<string>} - A promise that resolves to the dimensions (e.g., "1920x1080").
 */
export const getImageDimensions = file => {
    return new Promise(resolve => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const dimensions = `${img.width}x${img.height}`;
            resolve(dimensions);
            URL.revokeObjectURL(img.src);
        };
    });
};

/**
 * Sanitizes the name of a file object by replacing all special characters in the base name with dashes,
 * while preserving the file extension if present.
 * @param {File} file - The file whose name is to be sanitized.
 * @returns {File} - A new File object with the sanitized name and the original file type.
 */
export const sanitizeFileName = file => {
    if (file === null) return null;

    const nameParts = file.name.split('.');
    const extension = nameParts.length > 1 ? nameParts.pop() : '';
    const baseName = nameParts.join('.');

    const sanitizedBaseName = baseName
        .replace(/[^a-zA-Z0-9\s]/g, '-')
        .replace(/\s+/g, '-')
        .trim();

    const sanitizedName = extension
        ? `${sanitizedBaseName}.${extension}`
        : sanitizedBaseName;

    return new File([file], sanitizedName, { type: file.type });
};
